// Main API Client
import axios from 'axios';
import { API_BASE_URL, getToken, removeToken } from './config';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor - Add token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Return the data directly if the API follows the standard format
    return response.data;
  },
  (error) => {
    const status = error.response?.status;
    
    // Handle 401 Unauthorized - token expired or invalid
    // Only clear token and redirect if we're sure it's an auth issue
    // Don't redirect on network errors or if we're already on login page
    if (status === 401) {
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === '/login'
        || currentPath === '/signup'
        || currentPath === '/forgot-password'
        || currentPath === '/verify-otp'
        || currentPath === '/reset-password';
      
      // Only clear token and redirect if not on auth pages
      if (!isAuthPage) {
        // Check if token exists - if it does, it's likely expired/invalid
        const token = getToken();
        if (token) {
          removeToken();
          // Use a small delay to allow React state to update
          setTimeout(() => {
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }, 100);
        }
      }
    }

    // Build error response with better messages based on status code
    let defaultMessage = 'An error occurred';
    
    if (!error.response) {
      // Network error - no response received
      defaultMessage = 'Unable to connect. Please check your internet connection.';
    } else if (status === 400) {
      defaultMessage = 'Invalid request. Please check your input.';
    } else if (status === 401) {
      defaultMessage = 'Invalid credentials. Please try again.';
    } else if (status === 403) {
      defaultMessage = 'Access denied. You don\'t have permission.';
    } else if (status === 404) {
      defaultMessage = 'Not found. Please check your request.';
    } else if (status === 409) {
      defaultMessage = 'This record already exists.';
    } else if (status === 422) {
      defaultMessage = 'Validation failed. Please check your input.';
    } else if (status === 429) {
      defaultMessage = 'Too many requests. Please wait and try again.';
    } else if (status >= 500) {
      defaultMessage = 'Server error. Please try again later.';
    }

    // Return error in consistent format
    const errorData = error.response?.data || {
      success: false,
      message: defaultMessage,
      error: error.message || 'Network error or server unavailable',
    };

    // Ensure message exists
    if (!errorData.message) {
      errorData.message = defaultMessage;
    }

    // Add status code to error data
    errorData.statusCode = status;

    return Promise.reject(errorData);
  }
);

export default apiClient;

